import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon/Icon';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import baseStyles from '../form-field-base.module.css';
import styles from './DurationPickerField.module.css';

type DurationPickerMode = 'minutes-seconds' | 'hours-minutes' | 'seconds';

interface DurationPickerFieldProps {
  id: string;
  label?: string;
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  containerClassName?: string;
  maxMinutes?: number;
  showSeconds?: boolean;
  pickerMode?: DurationPickerMode;
  dialogTitle?: string;
  dialogSubtitle?: string;
  emptyValueText?: string;
  defaultSeconds?: number;
  allowZero?: boolean;
}

const SECONDS_ONLY_MAX = 59;

const formatClockValue = (minutes: number, seconds: number) => (
  `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
);

const formatSecondsOnlyValue = (totalSeconds: number) => `${totalSeconds} s`;

const formatClockValueByMode = (firstUnit: number, secondUnit: number, pickerMode: DurationPickerMode) => {
  if (pickerMode === 'seconds') {
    return formatSecondsOnlyValue(secondUnit);
  }

  if (pickerMode === 'hours-minutes') {
    return `${String(firstUnit).padStart(2, '0')}:${String(secondUnit).padStart(2, '0')}:00`;
  }

  return formatClockValue(firstUnit, secondUnit);
};

const formatDurationSummary = (totalSeconds: number, pickerMode: DurationPickerMode) => {
  if (pickerMode === 'seconds') {
    return formatSecondsOnlyValue(totalSeconds);
  }

  if (totalSeconds <= 0) {
    return 'Sem intervalo';
  }

  if (pickerMode === 'hours-minutes') {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0 && minutes > 0) {
      return `${hours} h ${minutes} min`;
    }

    if (hours > 0) {
      return `${hours} h`;
    }

    return `${minutes} min`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0 && seconds > 0) {
    return `${minutes} min ${seconds} s`;
  }

  if (minutes > 0) {
    return `${minutes} min`;
  }

  return `${seconds} s`;
};

const clampTotalSeconds = (totalSeconds: number, maxMinutes: number, pickerMode: DurationPickerMode) => {
  const maxTotalSeconds = pickerMode === 'seconds'
    ? SECONDS_ONLY_MAX
    : pickerMode === 'hours-minutes'
      ? maxMinutes * 60
      : (maxMinutes * 60) + 59;

  return Math.min(Math.max(totalSeconds, 0), maxTotalSeconds);
};

const normalizePickerValue = (value: string | number | null | undefined) => String(value ?? '').trim();

const isExplicitNonNegativeInteger = (value: string) => /^(0|[1-9]\d*)$/.test(value);

const parseTotalSeconds = (value: string, maxMinutes: number, pickerMode: DurationPickerMode) => {
  const parsedValue = Number.parseInt(value, 10);

  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    return 0;
  }

  if (parsedValue === 0) {
    return 0;
  }

  return clampTotalSeconds(parsedValue, maxMinutes, pickerMode);
};

const splitDuration = (totalSeconds: number, pickerMode: DurationPickerMode) => {
  if (pickerMode === 'seconds') {
    return {
      firstUnit: 0,
      secondUnit: totalSeconds,
    };
  }

  if (pickerMode === 'hours-minutes') {
    return {
      firstUnit: Math.floor(totalSeconds / 3600),
      secondUnit: Math.floor((totalSeconds % 3600) / 60),
    };
  }

  return {
    firstUnit: Math.floor(totalSeconds / 60),
    secondUnit: totalSeconds % 60,
  };
};

const buildTotalSeconds = (firstUnit: number, secondUnit: number, pickerMode: DurationPickerMode) => {
  if (pickerMode === 'seconds') {
    return secondUnit;
  }

  if (pickerMode === 'hours-minutes') {
    return (firstUnit * 3600) + (secondUnit * 60);
  }

  return (firstUnit * 60) + secondUnit;
};

const parseDurationUnitInput = (value: string, maxValue: number) => {
  const maxDigits = Math.max(2, String(maxValue).length);
  const digitsOnly = value.replace(/\D+/g, '').slice(0, maxDigits);

  if (!digitsOnly) {
    return '';
  }

  const parsedValue = Number.parseInt(digitsOnly, 10);

  if (!Number.isFinite(parsedValue)) {
    return '';
  }

  return String(Math.min(parsedValue, maxValue));
};

const parseDurationUnitValue = (value: string, maxValue: number) => {
  const parsedValue = Number.parseInt(value, 10);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return 0;
  }

  return Math.min(parsedValue, maxValue);
};

const DurationPickerField: React.FC<DurationPickerFieldProps> = ({
  id,
  label,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  placeholder = 'Configurar intervalo',
  containerClassName = '',
  maxMinutes = 59,
  showSeconds = true,
  pickerMode,
  dialogTitle = 'Configurar intervalo',
  dialogSubtitle,
  emptyValueText = 'Sem intervalo',
  defaultSeconds,
  allowZero = false,
}) => {
  const dialogTitleId = useId();
  const resolvedPickerMode = pickerMode ?? (showSeconds ? 'minutes-seconds' : 'hours-minutes');
  const isSecondsOnly = resolvedPickerMode === 'seconds';
  const normalizedValue = normalizePickerValue(value);
  const hasExplicitZero = allowZero
    && isExplicitNonNegativeInteger(normalizedValue)
    && Number.parseInt(normalizedValue, 10) === 0;
  const maxPrimaryUnitValue = resolvedPickerMode === 'hours-minutes'
    ? Math.max(0, Math.floor(maxMinutes / 60))
    : maxMinutes;
  const primaryInputMaxLength = Math.max(2, String(maxPrimaryUnitValue).length);
  const selectedTotalSeconds = useMemo(
    () => parseTotalSeconds(normalizedValue, maxMinutes, resolvedPickerMode),
    [maxMinutes, resolvedPickerMode, normalizedValue]
  );
  const draftFallbackSeconds = useMemo(() => {
    if (selectedTotalSeconds > 0 || hasExplicitZero) {
      return selectedTotalSeconds;
    }

    if (
      typeof defaultSeconds === 'number'
      && Number.isFinite(defaultSeconds)
      && (allowZero ? defaultSeconds >= 0 : defaultSeconds > 0)
    ) {
      return clampTotalSeconds(defaultSeconds, maxMinutes, resolvedPickerMode);
    }

    return 0;
  }, [allowZero, defaultSeconds, hasExplicitZero, maxMinutes, resolvedPickerMode, selectedTotalSeconds]);
  const [isOpen, setIsOpen] = useState(false);
  const [draftMinutes, setDraftMinutes] = useState('0');
  const [draftSeconds, setDraftSeconds] = useState('0');
  const currentPrimaryUnitValue = parseDurationUnitValue(draftMinutes, maxPrimaryUnitValue);
  const maxSecondaryUnitValue = isSecondsOnly
    ? SECONDS_ONLY_MAX
    : resolvedPickerMode === 'hours-minutes'
      ? (currentPrimaryUnitValue >= maxPrimaryUnitValue ? Math.max(0, maxMinutes - (maxPrimaryUnitValue * 60)) : 59)
      : 59;
  const secondaryInputMaxLength = Math.max(2, String(maxSecondaryUnitValue).length);

  const syncDraftFromValue = useCallback(() => {
    const { firstUnit, secondUnit } = splitDuration(draftFallbackSeconds, resolvedPickerMode);

    setDraftMinutes(String(firstUnit));
    setDraftSeconds(String(secondUnit));
  }, [draftFallbackSeconds, resolvedPickerMode]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isOpen]);

  const commitValue = useCallback((minutes: number, seconds: number) => {
    const totalSeconds = clampTotalSeconds(buildTotalSeconds(minutes, seconds, resolvedPickerMode), maxMinutes, resolvedPickerMode);

    onChange(totalSeconds > 0 ? String(totalSeconds) : (allowZero ? '0' : ''));
  }, [allowZero, maxMinutes, onChange, resolvedPickerMode]);

  const handleOpen = useCallback(() => {
    syncDraftFromValue();
    setIsOpen(true);
  }, [syncDraftFromValue]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);
  useEscapeToClose(handleClose, isOpen && !disabled);

  const applyPrimaryUnit = useCallback((nextPrimaryValue: string) => {
    setDraftMinutes(nextPrimaryValue);

    if (resolvedPickerMode === 'hours-minutes') {
      const nextParsedPrimaryValue = parseDurationUnitValue(nextPrimaryValue, maxPrimaryUnitValue);
      const nextMaxSecondaryUnitValue = nextParsedPrimaryValue >= maxPrimaryUnitValue
        ? Math.max(0, maxMinutes - (maxPrimaryUnitValue * 60))
        : 59;

      setDraftSeconds((currentValue) => parseDurationUnitInput(currentValue, nextMaxSecondaryUnitValue));
    }
  }, [maxMinutes, maxPrimaryUnitValue, resolvedPickerMode]);

  const handleMinuteChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    applyPrimaryUnit(parseDurationUnitInput(event.target.value, maxPrimaryUnitValue));
  }, [applyPrimaryUnit, maxPrimaryUnitValue]);

  const handleSecondChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftSeconds(parseDurationUnitInput(event.target.value, maxSecondaryUnitValue));
  }, [maxSecondaryUnitValue]);

  const stepDurationUnit = useCallback((currentValue: string, delta: number, maxValue: number) => {
    const nextValue = Math.min(maxValue, Math.max(0, parseDurationUnitValue(currentValue, maxValue) + delta));
    return String(nextValue);
  }, []);

  const handlePrimaryStep = useCallback((delta: number) => {
    applyPrimaryUnit(stepDurationUnit(draftMinutes, delta, maxPrimaryUnitValue));
  }, [applyPrimaryUnit, draftMinutes, maxPrimaryUnitValue, stepDurationUnit]);

  const handleSecondaryStep = useCallback((delta: number) => {
    setDraftSeconds(stepDurationUnit(draftSeconds, delta, maxSecondaryUnitValue));
  }, [draftSeconds, maxSecondaryUnitValue, stepDurationUnit]);

  const handlePrimaryKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      handlePrimaryStep(1);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      handlePrimaryStep(-1);
    }
  }, [handlePrimaryStep]);

  const handleSecondaryKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      handleSecondaryStep(1);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      handleSecondaryStep(-1);
    }
  }, [handleSecondaryStep]);

  const handleApply = useCallback(() => {
    const minutes = parseDurationUnitValue(draftMinutes, maxPrimaryUnitValue);
    const seconds = parseDurationUnitValue(draftSeconds, maxSecondaryUnitValue);

    commitValue(minutes, seconds);
    setIsOpen(false);
  }, [commitValue, draftMinutes, draftSeconds, maxPrimaryUnitValue, maxSecondaryUnitValue]);

  const handleClear = useCallback(() => {
    setDraftMinutes('0');
    setDraftSeconds('0');
    onChange('');
    setIsOpen(false);
  }, [onChange]);

  const handleBackdropMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setIsOpen(false);
    }
  }, []);

  const formattedClockValue = selectedTotalSeconds > 0 || hasExplicitZero
    ? (() => {
      const { firstUnit, secondUnit } = splitDuration(selectedTotalSeconds, resolvedPickerMode);

      return formatClockValueByMode(firstUnit, secondUnit, resolvedPickerMode);
    })()
    : null;
  const draftTotalSeconds = useMemo(() => {
    const minutes = parseDurationUnitValue(draftMinutes, maxPrimaryUnitValue);
    const seconds = parseDurationUnitValue(draftSeconds, maxSecondaryUnitValue);

    return clampTotalSeconds(buildTotalSeconds(minutes, seconds, resolvedPickerMode), maxMinutes, resolvedPickerMode);
  }, [draftMinutes, draftSeconds, maxMinutes, maxPrimaryUnitValue, maxSecondaryUnitValue, resolvedPickerMode]);
  //const resolvedDialogSubtitle = dialogSubtitle ?? (resolvedPickerMode === 'hours-minutes' ? 'Informe horas e minutos' : 'Informe minutos e segundos');
  const resolvedDialogSubtitle = "";
  const primaryUnitLabel = resolvedPickerMode === 'hours-minutes' ? 'Horas' : 'Minutos';
  const primaryUnitSuffix = resolvedPickerMode === 'hours-minutes' ? 'h' : 'min';
  const secondaryUnitLabel = resolvedPickerMode === 'hours-minutes' ? 'Minutos' : 'Segundos';
  const secondaryUnitSuffix = resolvedPickerMode === 'hours-minutes' ? 'min' : 's';
  const selectedPreviewValue = isSecondsOnly
    ? formatDurationSummary(draftTotalSeconds, resolvedPickerMode)
    : `${formatClockValueByMode(parseDurationUnitValue(draftMinutes, maxPrimaryUnitValue), parseDurationUnitValue(draftSeconds, maxSecondaryUnitValue), resolvedPickerMode)} • ${formatDurationSummary(draftTotalSeconds, resolvedPickerMode)}`;
  const emptyPreviewValue = allowZero
    ? (isSecondsOnly ? '0 s' : '00:00 • 0 s')
    : emptyValueText;

  const triggerClassName = [
    styles.trigger,
    isOpen ? styles.triggerOpen : '',
    error ? styles.triggerError : '',
    disabled ? styles.triggerDisabled : '',
  ].filter(Boolean).join(' ');

  const modalContent = isOpen && !disabled ? createPortal(
    <div className={styles.modalBackdrop} role="presentation" onMouseDown={handleBackdropMouseDown}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby={dialogTitleId}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitleBlock}>
            <span className={styles.modalIcon} aria-hidden="true">
              <Icon name="schedule" size={22} color="var(--color-accent-brown)" />
            </span>

            <div className={styles.modalText}>
              <span id={dialogTitleId} className={styles.modalTitle}>{dialogTitle}</span>
              <span className={styles.modalSubtitle}>{resolvedDialogSubtitle}</span>
            </div>
          </div>

          <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Fechar modal">
            <Icon name="close" size={18} color="var(--color-text-muted)" />
          </button>
        </div>

        <div className={`${styles.inputGrid} ${isSecondsOnly ? styles.inputGridSingleColumn : ''}`.trim()}>
          {isSecondsOnly ? null : (
          <label className={styles.inputField} htmlFor={`${id}-minutes`}>
            <span className={styles.inputLabel}>{primaryUnitLabel}</span>
            <div className={styles.numericInputWrapper}>
              <input
                id={`${id}-minutes`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={primaryInputMaxLength}
                className={styles.numericInput}
                value={draftMinutes}
                onChange={handleMinuteChange}
                onKeyDown={handlePrimaryKeyDown}
                placeholder="00"
              />
              <span className={styles.numericSuffix}>{primaryUnitSuffix}</span>
              <div className={styles.stepper}>
                <button
                  type="button"
                  className={styles.stepperButton}
                  onClick={() => handlePrimaryStep(1)}
                  disabled={currentPrimaryUnitValue >= maxPrimaryUnitValue}
                  aria-label={`Aumentar ${primaryUnitLabel.toLowerCase()}`}
                >
                  <Icon name="keyboard_arrow_up" size={16} />
                </button>
                <button
                  type="button"
                  className={styles.stepperButton}
                  onClick={() => handlePrimaryStep(-1)}
                  disabled={currentPrimaryUnitValue <= 0}
                  aria-label={`Diminuir ${primaryUnitLabel.toLowerCase()}`}
                >
                  <Icon name="keyboard_arrow_down" size={16} />
                </button>
              </div>
            </div>
          </label>
          )}

          <label className={styles.inputField} htmlFor={`${id}-seconds`}>
            <span className={styles.inputLabel}>{secondaryUnitLabel}</span>
            <div className={styles.numericInputWrapper}>
              <input
                id={`${id}-seconds`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={secondaryInputMaxLength}
                className={styles.numericInput}
                value={draftSeconds}
                onChange={handleSecondChange}
                onKeyDown={handleSecondaryKeyDown}
                placeholder="00"
              />
              <span className={styles.numericSuffix}>{secondaryUnitSuffix}</span>
              <div className={styles.stepper}>
                <button
                  type="button"
                  className={styles.stepperButton}
                  onClick={() => handleSecondaryStep(1)}
                  disabled={parseDurationUnitValue(draftSeconds, maxSecondaryUnitValue) >= maxSecondaryUnitValue}
                  aria-label={`Aumentar ${secondaryUnitLabel.toLowerCase()}`}
                >
                  <Icon name="keyboard_arrow_up" size={16} />
                </button>
                <button
                  type="button"
                  className={styles.stepperButton}
                  onClick={() => handleSecondaryStep(-1)}
                  disabled={parseDurationUnitValue(draftSeconds, maxSecondaryUnitValue) <= 0}
                  aria-label={`Diminuir ${secondaryUnitLabel.toLowerCase()}`}
                >
                  <Icon name="keyboard_arrow_down" size={16} />
                </button>
              </div>
            </div>
          </label>
        </div>

        <div className={styles.previewCard}>
          <span className={styles.previewLabel}>Selecionado</span>
          <span className={styles.previewValue}>
            {draftTotalSeconds > 0 || (allowZero && isSecondsOnly)
              ? selectedPreviewValue
              : emptyPreviewValue}
          </span>
        </div>

        <div className={styles.modalActions}>
          <button type="button" className={styles.secondaryButton} onClick={handleClear}>
            Limpar
          </button>
          <button type="button" className={styles.secondaryButton} onClick={handleClose}>
            Cancelar
          </button>
          <button type="button" className={styles.primaryButton} onClick={handleApply}>
            Aplicar
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={`${baseStyles.fieldGroup} ${containerClassName}`}>
      {label && (
        <label htmlFor={id} className={baseStyles.label}>
          {label}
          {required && <span className={baseStyles.required}>*</span>}
        </label>
      )}

      <div className={styles.wrapper}>
        <button
          id={id}
          type="button"
          className={triggerClassName}
          onClick={() => !disabled && handleOpen()}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={isOpen ? dialogTitleId : undefined}
        >
          <span className={styles.triggerContent}>
            <span className={styles.iconBadge} aria-hidden="true">
              <Icon name="schedule" size={18} color="var(--color-accent-brown)" />
            </span>

            <span className={styles.triggerText}>
              <span className={formattedClockValue ? styles.triggerValue : styles.triggerPlaceholder}>
                {formattedClockValue ?? placeholder}
              </span>
            </span>
          </span>

          <Icon
            name={isOpen ? 'expand_less' : 'expand_more'}
            size={20}
            color="var(--color-text-muted)"
            className={styles.chevron}
          />
        </button>
      </div>

      {modalContent}

      {error && <span className={baseStyles.errorText}>{error}</span>}
    </div>
  );
};

export default DurationPickerField;